import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { 
  TrendingDown, 
  TrendingUp, 
  AlertTriangle, 
  Sparkles,
  Copy,
  X
} from 'lucide-react';

interface AtRiskAccount {
  customer_name: string;
  health_score: number;
  risk_factors: string[];
  mitigation_plan: string;
}

interface ThrivingAccount {
  customer_name: string;
  health_score: number;
  success_indicators: string[];
  expansion_opportunities: string;
}

interface ProductGap {
  gap_description: string;
  impact: string;
  customer_examples: string[];
}

interface WeeklyDigestData {
  summary: string;
  at_risk_accounts: AtRiskAccount[];
  thriving_accounts: ThrivingAccount[];
  product_gaps: ProductGap[];
  start_date: string;
  end_date: string;
  generated_at: string;
}

interface WeeklyDigestModalProps {
  isOpen: boolean;
  onClose: () => void;
  digestData: WeeklyDigestData | null;
}

export const WeeklyDigestModal: React.FC<WeeklyDigestModalProps> = ({
  isOpen,
  onClose,
  digestData,
}) => {
  const copyToClipboard = () => {
    if (!digestData) return;
    
    let text = `Weekly Leadership Digest (${digestData.start_date} to ${digestData.end_date})\n\n`;
    text += `Summary: ${digestData.summary}\n\n`;
    
    text += `=== AT-RISK ACCOUNTS (${digestData.at_risk_accounts?.length || 0}) ===\n`;
    (digestData.at_risk_accounts || []).forEach((account, idx) => {
      text += `\n${idx + 1}. ${account.customer_name} (Health: ${account.health_score})\n`;
      text += `   Risk Factors: ${(account.risk_factors || []).join(', ')}\n`;
      text += `   Mitigation: ${account.mitigation_plan || 'N/A'}\n`;
    });
    
    text += `\n=== THRIVING ACCOUNTS (${digestData.thriving_accounts?.length || 0}) ===\n`;
    (digestData.thriving_accounts || []).forEach((account, idx) => {
      text += `\n${idx + 1}. ${account.customer_name} (Health: ${account.health_score})\n`;
      text += `   Success: ${(account.success_indicators || []).join(', ')}\n`;
      text += `   Opportunities: ${account.expansion_opportunities || 'N/A'}\n`;
    });
    
    text += `\n=== PRODUCT GAPS (${digestData.product_gaps?.length || 0}) ===\n`;
    (digestData.product_gaps || []).forEach((gap, idx) => {
      text += `\n${idx + 1}. ${gap.gap_description || 'N/A'}\n`;
      text += `   Impact: ${gap.impact || 'N/A'}\n`;
      text += `   Examples: ${(gap.customer_examples || []).join(', ')}\n`;
    });
    
    navigator.clipboard.writeText(text);
  };

  if (!isOpen || !digestData) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl border-0 bg-card/95 backdrop-blur-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6 border-b border-border">
          <div className="flex items-center space-x-2">
            <Sparkles className="h-6 w-6 text-purple-500" />
            <CardTitle className="text-2xl font-bold text-foreground">Weekly Leadership Digest</CardTitle>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={copyToClipboard}
              className="flex items-center space-x-2"
            >
              <Copy className="h-4 w-4" />
              <span>Copy</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} className="hover:bg-muted rounded-full">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="pt-6">
          <div className="mb-4">
            <p className="text-sm text-muted-foreground">
              {digestData.start_date} to {digestData.end_date}
            </p>
          </div>

          <div className="space-y-6">
          {/* Summary */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <p className="text-foreground">{digestData.summary}</p>
          </div>

          {/* At-Risk Accounts */}
          {digestData.at_risk_accounts && digestData.at_risk_accounts.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <TrendingDown className="h-5 w-5 text-red-500" />
                <h3 className="text-lg font-semibold text-foreground">
                  At-Risk Accounts ({digestData.at_risk_accounts.length})
                </h3>
              </div>
              <div className="space-y-3">
                {digestData.at_risk_accounts.map((account, idx) => (
                  <div
                    key={idx}
                    className="bg-card border border-red-200 dark:border-red-800 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-foreground">{account.customer_name}</h4>
                      <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded text-sm">
                        Health: {account.health_score}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium text-foreground">Risk Factors:</span>
                        <ul className="list-disc list-inside ml-2 text-muted-foreground">
                          {(account.risk_factors || []).map((factor, i) => (
                            <li key={i}>{factor}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <span className="font-medium text-foreground">Mitigation Plan:</span>
                        <p className="ml-2 text-muted-foreground">{account.mitigation_plan}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Thriving Accounts */}
          {digestData.thriving_accounts && digestData.thriving_accounts.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5 text-green-500" />
                <h3 className="text-lg font-semibold text-foreground">
                  Thriving Accounts ({digestData.thriving_accounts.length})
                </h3>
              </div>
              <div className="space-y-3">
                {digestData.thriving_accounts.map((account, idx) => (
                  <div
                    key={idx}
                    className="bg-card border border-green-200 dark:border-green-800 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-foreground">{account.customer_name}</h4>
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-sm">
                        Health: {account.health_score}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium text-foreground">Success Indicators:</span>
                        <ul className="list-disc list-inside ml-2 text-muted-foreground">
                          {(account.success_indicators || []).map((indicator, i) => (
                            <li key={i}>{indicator}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <span className="font-medium text-foreground">Expansion Opportunities:</span>
                        <p className="ml-2 text-muted-foreground">{account.expansion_opportunities}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Product Gaps */}
          {digestData.product_gaps && digestData.product_gaps.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-blue-500" />
                <h3 className="text-lg font-semibold text-foreground">
                  Product Gaps ({digestData.product_gaps.length})
                </h3>
              </div>
              <div className="space-y-3">
                {digestData.product_gaps.map((gap, idx) => (
                  <div
                    key={idx}
                    className="bg-card border border-blue-200 dark:border-blue-800 rounded-lg p-4"
                  >
                    <h4 className="font-semibold text-foreground mb-2">{gap.gap_description}</h4>
                    <div className="space-y-1 text-sm">
                      <p>
                        <span className="font-medium text-foreground">Impact:</span>{' '}
                        <span className="text-muted-foreground">{gap.impact}</span>
                      </p>
                      <p>
                        <span className="font-medium text-foreground">Affected Customers:</span>{' '}
                        <span className="text-muted-foreground">{(gap.customer_examples || []).join(', ')}</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

          <div className="flex justify-end space-x-2 pt-4 border-t border-border">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

